import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { getStats, AdminStats } from '../../api/admin';

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await getStats();
      setStats(data);
      setError(null);
    } catch (err: any) {
      console.error('[AdminDashboard] Error loading stats:', err);
      setError(err.response?.data?.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>

      {/* Quick Actions */}
      <Paper elevation={2} sx={{ mb: 3, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<PeopleIcon />}
            onClick={() => navigate('/admin/users')}
          >
            Manage Users
          </Button>
          <Button
            variant="contained"
            startIcon={<FamilyRestroomIcon />}
            onClick={() => navigate('/admin/families')}
          >
            Manage Families
          </Button>
        </Box>
      </Paper>

      {/* System Statistics */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        System Overview
      </Typography>

      <Grid container spacing={3}>
        {/* Users Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h6">Users</Typography>
              </Box>
              <Typography variant="h3" gutterBottom>
                {stats.users.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Parents: {stats.users.parents} | Staff: {stats.users.staff} | Admins:{' '}
                {stats.users.admins}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Families Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FamilyRestroomIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h6">Families</Typography>
              </Box>
              <Typography variant="h3" gutterBottom>
                {stats.families}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total registered families
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Students Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SchoolIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h6">Students</Typography>
              </Box>
              <Typography variant="h3" gutterBottom>
                {stats.registered_students}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total registered students
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Activity Card */}
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h6">Today</Typography>
              </Box>
              <Typography variant="h3" gutterBottom>
                {stats.today.check_ins}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check-ins: {stats.today.check_ins} | Dismissals: {stats.today.dismissals}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Currently waiting: {stats.today.currently_checked_in}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity Section (placeholder) */}
      <Paper elevation={2} sx={{ mt: 4, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Activity
        </Typography>
        <Alert severity="info">
          Activity log feature coming soon. Check-ins today: {stats.today.check_ins}, Dismissals:{' '}
          {stats.today.dismissals}
        </Alert>
      </Paper>
    </Container>
  );
};

export default AdminDashboardPage;
